import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useState } from 'react';

const OurApproach = () => {
  const [activeStep, setActiveStep] = useState(0);

  const communityFactors = [
    { icon: 'ri-heart-pulse-line', text: 'Chronic health conditions', color: 'bg-[#f04e58]' },
    { icon: 'ri-dna-line', text: 'Hereditary health risks', color: 'bg-[#0d9488]' },
    { icon: 'ri-global-line', text: 'Ethnicity and cultural background', color: 'bg-[#f04e58]' },
    { icon: 'ri-user-line', text: 'Age and gender', color: 'bg-[#0d9488]' },
    { icon: 'ri-map-pin-line', text: 'Geographic location', color: 'bg-[#f04e58]' },
    { icon: 'ri-run-line', text: 'Exercise routines and lifestyle', color: 'bg-[#0d9488]' },
    { icon: 'ri-palette-line', text: 'Hobbies, interests, and preferences', color: 'bg-[#f04e58]' }
  ];

  const communityBenefits = [
    'Members support one another through challenges',
    'Progress is shared and normalized',
    'Healthy habits become easier to maintain',
    'Motivation comes from peers, not pressure'
  ];

  const professionalSupport = [
    {
      icon: 'ri-team-line',
      title: 'Employee Ambassadors',
      description: 'A small group of motivated employee ambassadors'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Ongoing Coaching',
      description: 'Continuous coaching for ambassadors'
    },
    {
      icon: 'ri-stethoscope-line',
      title: 'Medical Moderation',
      description: 'Certified medical professionals monitoring discussions'
    }
  ];

  const approachCycle = [
    {
      step: 1,
      title: 'Join Community',
      description: 'Employees are matched into communities based on shared characteristics and health journeys',
      icon: 'ri-team-line',
      color: '#f04e58'
    },
    {
      step: 2,
      title: 'Connect & Share',
      description: 'Members connect with like-minded peers, share experiences, and build supportive relationships',
      icon: 'ri-chat-heart-line',
      color: '#0d9488'
    },
    {
      step: 3,
      title: 'Stay Motivated',
      description: 'Peer support and accountability help maintain healthy habits and consistent routines',
      icon: 'ri-heart-pulse-line',
      color: '#f04e58'
    },
    {
      step: 4,
      title: 'Track Progress',
      description: 'Progress is shared and celebrated, making health goals more achievable together',
      icon: 'ri-line-chart-line',
      color: '#0d9488'
    },
    {
      step: 5,
      title: 'Get Guidance',
      description: 'Medical professionals provide evidence-based guidance and keep conversations accurate',
      icon: 'ri-stethoscope-line',
      color: '#f04e58'
    },
    {
      step: 6,
      title: 'Sustain Health',
      description: 'Long-term health outcomes improve through continuous community support and motivation',
      icon: 'ri-shield-check-line',
      color: '#0d9488'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 bg-[#fef3f2]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block mb-4 md:mb-6">
              <span className="px-4 md:px-6 py-2 md:py-3 bg-[#f04e58] text-white rounded-full text-sm font-semibold shadow-lg">
                Our Approach
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-gray-900 mb-6 md:mb-8 leading-tight">
              Sustained Health Through <strong className="font-bold text-[#f04e58]">Community</strong>
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-gray-600 font-light leading-relaxed">
              Managing a chronic condition isn't difficult for a day or a week—it's difficult over a lifetime.
            </p>
          </div>
        </div>
      </section>

      {/* The Vazcular Approach Cycle */}
      <section className="py-16 md:py-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-20">
            <h2 className="text-3xl md:text-5xl font-light text-gray-900 mb-4 md:mb-6 leading-tight">
              The <strong className="font-bold text-[#f04e58]">Vazcular Approach</strong> Cycle
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed max-w-3xl mx-auto">
              A continuous journey of community support, motivation, and sustained health outcomes
            </p>
          </div>

          {/* Desktop: Circular Cycle Layout */}
          <div className="hidden lg:block relative max-w-5xl mx-auto">
            {/* Center Circle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[#fef3f2] border-4 border-[#f04e58]/20 flex items-center justify-center z-10">
              <div className="text-center">
                <i className="ri-refresh-line text-5xl text-[#f04e58] mb-3"></i>
                <p className="text-xl font-bold text-gray-900">Continuous</p>
                <p className="text-lg text-gray-600">Support Cycle</p>
              </div>
            </div>

            {/* Cycle Cards */}
            <div className="relative w-full h-[800px]">
              {approachCycle.map((item, index) => {
                const angle = (index * 60 - 90) * (Math.PI / 180);
                const radius = 320;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;

                return (
                  <div
                    key={index}
                    className="absolute top-1/2 left-1/2 cursor-pointer"
                    style={{
                      transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={() => setActiveStep(index)}
                  >
                    <div
                      className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 w-56 border-2 ${
                        activeStep === index ? 'border-[#f04e58] scale-110' : 'border-gray-100'
                      }`}
                    >
                      {/* Step Number */}
                      <div className="flex items-center justify-between mb-4">
                        <div
                          className="w-12 h-12 flex items-center justify-center rounded-full text-white font-bold text-lg"
                          style={{ backgroundColor: item.color }}
                        >
                          {item.step}
                        </div>
                        <div
                          className="w-10 h-10 flex items-center justify-center rounded-lg"
                          style={{ backgroundColor: `${item.color}15` }}
                        >
                          <i className={`${item.icon} text-2xl`} style={{ color: item.color }}></i>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>

                      {/* Description */}
                      <p className="text-sm text-gray-600 font-light leading-relaxed">
                        {item.description}
                      </p>

                      {/* Active Indicator */}
                      {activeStep === index && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="w-full h-1 rounded-full" style={{ backgroundColor: item.color }}></div>
                        </div>
                      )}
                    </div>

                    {/* Connecting Line to Center */}
                    <svg
                      className="absolute top-1/2 left-1/2 pointer-events-none"
                      style={{
                        width: Math.abs(x) * 2,
                        height: Math.abs(y) * 2,
                        transform: `translate(-50%, -50%)`,
                        zIndex: -1
                      }}
                    >
                      <line
                        x1={x > 0 ? 0 : Math.abs(x) * 2}
                        y1={y > 0 ? 0 : Math.abs(y) * 2}
                        x2={Math.abs(x)}
                        y2={Math.abs(y)}
                        stroke={activeStep === index ? item.color : '#e5e7eb'}
                        strokeWidth="2"
                        strokeDasharray={activeStep === index ? '0' : '5,5'}
                        opacity={activeStep === index ? '0.5' : '0.2'}
                      />
                    </svg>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile/Tablet: Grid Layout */}
          <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
            {approachCycle.map((item, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-5 shadow-lg border-2 transition-all duration-300 cursor-pointer ${
                  activeStep === index ? 'border-[#f04e58]' : 'border-gray-100'
                }`}
                onClick={() => setActiveStep(index)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-full text-white font-bold text-base"
                    style={{ backgroundColor: item.color }}
                  >
                    {item.step}
                  </div>
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                  </div>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600 font-light leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Step Navigation Dots */}
          <div className="flex justify-center gap-3 mt-8 md:mt-12">
            {approachCycle.map((item, index) => (
              <button
                key={index}
                onClick={() => setActiveStep(index)}
                className={`h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  activeStep === index ? 'w-8' : 'w-3'
                }`}
                style={{
                  backgroundColor: activeStep === index ? item.color : '#d1d5db'
                }}
                aria-label={`View step ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Guided by Professionals */}
      <section className="py-16 md:py-24 bg-[#fef3f2]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-light text-gray-900 mb-4 md:mb-6 leading-tight">
              Guided by <strong className="font-bold text-[#f04e58]">Professionals</strong>, Centered on Employees
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed max-w-2xl mx-auto">
              Each community includes:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12">
            {professionalSupport.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#f04e58]/10 mb-5">
                  <i className={`${item.icon} text-2xl md:text-3xl text-[#f04e58]`}></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 font-light leading-relaxed text-sm md:text-base">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl p-6 md:p-12 shadow-xl border border-gray-100">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Medical professionals:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 mb-8">
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mx-auto mb-4">
                  <i className="ri-eye-line text-2xl text-[#0d9488]"></i>
                </div>
                <p className="text-gray-700 font-light text-sm md:text-base">Monitor discussions</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mx-auto mb-4">
                  <i className="ri-lightbulb-line text-2xl text-[#0d9488]"></i>
                </div>
                <p className="text-gray-700 font-light text-sm md:text-base">Provide evidence-based guidance when needed</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-[#0d9488]"></i>
                </div>
                <p className="text-gray-700 font-light text-sm md:text-base">Help keep conversations accurate and responsible</p>
              </div>
            </div>
            <div className="bg-[#f0fdfa] rounded-2xl p-5 md:p-6 border-2 border-[#0d9488]/20">
              <p className="text-gray-700 font-light leading-relaxed text-center text-sm md:text-base">
                <strong className="font-semibold text-[#0d9488]">Important:</strong> They do not replace physicians. Vazcular does not diagnose or treat medical conditions. Employees are always encouraged to consult their own primary care providers or specialists for medical decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-light text-gray-900 mb-6 md:mb-8 leading-tight">
              The <strong className="font-bold text-[#0d9488]">Challenge</strong>
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed max-w-3xl mx-auto">
              Over time, many individuals lose motivation to consistently follow strict routines around medication, diet, exercise, and lifestyle changes. When health becomes a long-term commitment, isolation and fatigue often take over.
            </p>
          </div>

          <div className="bg-[#f0fdfa] rounded-3xl p-8 md:p-12 text-center border-2 border-[#0d9488]/20">
            <div className="w-16 md:w-20 h-16 md:h-20 flex items-center justify-center rounded-2xl bg-[#0d9488] mx-auto mb-6">
              <i className="ri-team-line text-3xl md:text-4xl text-white"></i>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Vazcular addresses this challenge by replacing isolation with connection.
            </h3>
          </div>
        </div>
      </section>

      {/* Employee Community Approach */}
      <section className="py-16 md:py-24 bg-[#fef3f2]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-light text-gray-900 mb-4 md:mb-6 leading-tight">
              The <strong className="font-bold text-[#f04e58]">Employee Community</strong> Approach
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed max-w-3xl mx-auto">
              Vazcular builds purpose-driven employee health communities by thoughtfully grouping employees based on shared characteristics such as:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
            {communityFactors.map((factor, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-5 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${factor.color} flex-shrink-0`}>
                    <i className={`${factor.icon} text-xl md:text-2xl text-white`}></i>
                  </div>
                  <p className="text-gray-900 font-medium leading-snug text-sm md:text-base">{factor.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl p-6 md:p-12 shadow-xl border border-gray-100">
            <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#f04e58]/10 mb-5">
                  <i className="ri-team-line text-2xl md:text-3xl text-[#f04e58]"></i>
                </div>
                <p className="text-base md:text-xl text-gray-700 font-light leading-relaxed">
                  Employees are placed into communities where they naturally fit—surrounded by <strong className="font-semibold text-[#f04e58]">like-minded peers</strong> facing similar health journeys.
                </p>
              </div>

              <div className="flex items-center justify-center">
                <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#0d9488]/10 mb-5">
                  <i className="ri-heart-pulse-line text-2xl md:text-3xl text-[#0d9488]"></i>
                </div>
                <p className="text-base md:text-xl text-gray-700 font-light leading-relaxed">
                  This shared experience creates <strong className="font-semibold text-[#0d9488]">accountability, encouragement, and consistency</strong>—helping everyone stay on track together.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Motivation That Lasts */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-light text-gray-900 mb-4 md:mb-6 leading-tight">
              Motivation That <strong className="font-bold text-[#0d9488]">Lasts</strong>
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed max-w-2xl mx-auto">
              Within each community:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-12">
            {communityBenefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-4 bg-[#f0fdfa] rounded-2xl p-6 md:p-8 border-2 border-[#0d9488]/20 hover:border-[#0d9488] transition-all duration-300"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] flex-shrink-0 mt-1">
                  <i className="ri-check-line text-lg text-white"></i>
                </div>
                <p className="text-base md:text-lg text-gray-900 font-light leading-relaxed">{benefit}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#fef3f2] rounded-3xl p-7 md:p-10 text-center border-2 border-[#f04e58]/20">
            <p className="text-base md:text-lg text-gray-700 font-light leading-relaxed">
              If a community doesn't feel like the right fit, employees can request reassignment at any time. Vazcular is designed to be <strong className="font-semibold text-[#f04e58]">flexible, inclusive, and human-centered</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Business Model */}
      <section className="py-16 md:py-24 bg-[#fef3f2]">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-light text-gray-900 mb-4 md:mb-6 leading-tight">
              A Simple, <strong className="font-bold text-[#0d9488]">Scalable</strong> Business Model
            </h2>
            <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed max-w-3xl mx-auto">
              Vazcular operates on a monthly employer-paid subscription model:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
            <div className="bg-[#f0fdfa] rounded-2xl p-6 md:p-8 text-center border-2 border-[#0d9488]/20">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#0d9488] mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-2xl md:text-3xl text-white"></i>
              </div>
              <p className="text-gray-900 font-medium text-sm md:text-base">A small fee per employee enrolled in a community</p>
            </div>
            <div className="bg-[#f0fdfa] rounded-2xl p-6 md:p-8 text-center border-2 border-[#0d9488]/20">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#0d9488] mx-auto mb-4">
                <i className="ri-line-chart-line text-2xl md:text-3xl text-white"></i>
              </div>
              <p className="text-gray-900 font-medium text-sm md:text-base">Predictable and scalable across organizations</p>
            </div>
            <div className="bg-[#f0fdfa] rounded-2xl p-6 md:p-8 text-center border-2 border-[#0d9488]/20">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#0d9488] mx-auto mb-4">
                <i className="ri-heart-pulse-line text-2xl md:text-3xl text-white"></i>
              </div>
              <p className="text-gray-900 font-medium text-sm md:text-base">Healthier employees and reduced long-term healthcare costs</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-7 md:p-10 text-center border-2 border-[#f04e58]/20 shadow-lg">
            <p className="text-base md:text-xl text-gray-700 font-light leading-relaxed">
              By improving consistency, engagement, and prevention, employers benefit from <strong className="font-semibold text-[#f04e58]">healthier employees and reduced long-term healthcare costs</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* The Vazcular Difference */}
      <section className="py-20 md:py-32 bg-[#f04e58] relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-block mb-6">
            <span className="px-4 md:px-6 py-2 md:py-3 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold">
              The Vazcular Difference
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-light text-white mb-6 md:mb-8 leading-tight">
            From Solo Responsibility to <strong className="font-bold">Shared Journey</strong>
          </h2>
          <p className="text-base md:text-xl text-white/90 mb-8 md:mb-12 font-light leading-relaxed max-w-3xl mx-auto">
            Vazcular turns chronic care from a solo responsibility into a shared journey—where community, motivation, and guidance work together to create better, more sustainable health outcomes.
          </p>
          <button
            onClick={() => {
              const modal = document.querySelector('[data-readdy-form]');
              if (modal) {
                const bookButton = document.querySelector('button:has(> i.ri-arrow-right-line)') as HTMLElement;
                if (bookButton) bookButton.click();
              }
            }}
            className="inline-flex items-center px-7 md:px-10 py-4 md:py-5 bg-white text-[#f04e58] rounded-full text-base md:text-lg font-semibold hover:bg-gray-50 transition-all shadow-2xl hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            Book Your Free Consultation
            <i className="ri-arrow-right-line ml-3 text-lg md:text-xl"></i>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OurApproach;
